import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { MapPin, AlertTriangle, Clock, Activity, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { attendanceAPI } from '@/services/attendance';


const AttendancePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  
  // GPS State
  const [gpsActive, setGpsActive] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Timer State
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    fetchRecords();

    // Initialize GPS Watcher
    let watchId;
    if ('geolocation' in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          setGpsActive(true);
          setGpsError(null);
          setCurrentLocation(position.coords);
        },
        (error) => {
          setGpsActive(false);
          let msg = "GPS unavailable";
          if (error.code === 1) msg = "Location permission denied";
          else if (error.code === 2) msg = "Position unavailable";
          else if (error.code === 3) msg = "Location timeout";
          setGpsError(msg);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setGpsError("Geolocation not supported by this browser.");
    }

    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const fetchRecords = async () => {
    try {
      const response = await attendanceAPI.list(user.id);
      
      if (response.success) {
        setRecords(response.records || []);
        
        const activeSession = response.records.find(r => r.clock_in && !r.clock_out);
        if (activeSession && activeSession.clock_in_timestamp && response.server_time) {
          const clockInTimestamp = parseInt(activeSession.clock_in_timestamp);
          const serverTimestamp = parseInt(response.server_time);
          const diffSeconds = serverTimestamp - clockInTimestamp;
          
          if (diffSeconds >= 0 && diffSeconds < 60) {
            setCountdown(60 - diffSeconds);
          } else {
            setCountdown(0);
          }
        } else {
          setCountdown(0);
        }
      }
    } catch (error) {
      console.error('Failed to fetch records:', error);
    }
  };

  const handleClockAction = async (type) => {
    if (!gpsActive || !currentLocation) {
      toast({ 
        title: "GPS Required", 
        description: "Please enable location services to proceed.", 
        variant: "destructive" 
      });
      return;
    }

    if (type === 'out' && countdown > 0) {
      toast({
        title: "⏱️ Too Soon!",
        description: `You need to wait ${countdown} more seconds before clocking out.`,
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        userId: user.id,
        userName: user.name,
        locationLat: currentLocation.latitude,
        locationLng: currentLocation.longitude
      };

      let response;
      if (type === 'in') {
        response = await attendanceAPI.clockIn(payload);
        
        if (response.success) {
          setCountdown(60);
          toast({
            title: "✅ Clocked In Successfully!",
            description: "Your attendance has been recorded.",
            className: "bg-green-50 border-green-200",
            duration: 3000
          });
        }
      } else {
        response = await attendanceAPI.clockOut(payload);
        
        if (response.success) {
          toast({
            title: "✅ Clocked Out Successfully!",
            description: `Work hours: ${response.record?.work_hours || 0} hours`,
            className: "bg-blue-50 border-blue-200",
            duration: 3000
          });
        }
      }

      if (response.success) {
        await fetchRecords();
      }

    } catch (e) {
      console.error('Clock action error:', e);
      
      let errorTitle = "❌ Error";
      let errorMessage = "Failed to process clock action";
      
      if (e.message.includes("1 minute") || e.message.includes("Cannot clock out within")) {
        errorTitle = "⏱️ Too Soon";
        errorMessage = `Please wait ${countdown} seconds before clocking out.`;
      } else if (e.message.includes("already have an active")) {
        errorTitle = "⚠️ Already Clocked In";
        errorMessage = "You already have an active clock-in for today.";
      } else if (e.message.includes("No active clock-in")) {
        errorTitle = "⚠️ No Active Clock-In";
        errorMessage = "Please clock in first before clocking out.";
      } else if (e.message) {
        errorMessage = e.message;
      }
      
      toast({ 
        title: errorTitle, 
        description: errorMessage,
        variant: "destructive",
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const isClockedIn = records.length > 0 && records[0].clock_in && !records[0].clock_out;
  const isClockOutDisabled = isClockedIn && countdown > 0;

  return (
    <>
      <Helmet><title>Attendance - Stone Beam</title></Helmet>
      <div className="space-y-6 max-w-xl mx-auto pb-12 p-4">
        
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-navy-steal">Attendance</h1>
          <div className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border",
            gpsActive ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"
          )}>
            {gpsActive ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {gpsActive ? "GPS Active" : "GPS Inactive"}
          </div>
        </div>

        {!gpsActive && (
          <div className="bg-red-50 border border-red-100 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 text-sm">Location Required</h4>
              <p className="text-sm text-red-700 mt-1">
                {gpsError || "Please enable GPS/Location services in your browser to check in."}
              </p>
            </div>
          </div>
        )}

        <Card className={cn("overflow-hidden border-t-4", isClockedIn ? "border-t-green-600" : "border-t-stone-400")}>
          <CardHeader>
            <CardTitle className="text-center flex flex-col items-center gap-2">
              <span className="text-stone-500 text-sm font-normal uppercase tracking-wider">Current Status</span>
              <span className={cn("text-2xl font-bold", isClockedIn ? "text-green-600" : "text-stone-700")}>
                {isClockedIn ? "CLOCKED IN" : "NOT WORKING"}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center space-y-6">
            {/* Location Display */}
            <div className="bg-slate-50 rounded-lg p-6 border-2 border-slate-200">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-blue-600" />
              {currentLocation ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Current Location</p>
                  <div className="font-mono text-xs text-slate-500 space-y-1">
                    <div>Lat: {currentLocation.latitude.toFixed(6)}</div>
                    <div>Lng: {currentLocation.longitude.toFixed(6)}</div>
                    <div className="text-[10px] text-slate-400 mt-2">
                      Accuracy: ±{currentLocation.accuracy?.toFixed(0)}m
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400">Waiting for GPS...</p>
              )}
            </div>
            
            {/* Countdown Display */}
            {isClockedIn && countdown > 0 && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                <Clock className="w-6 h-6 mx-auto mb-2 text-amber-600 animate-pulse" />
                <p className="text-3xl font-bold font-mono text-amber-700">{countdown}s</p>
                <p className="text-xs text-amber-600 mt-1">Wait before clocking out</p>
              </div>
            )}
            
            <Button 
              size="lg" 
              className={cn(
                "w-full text-lg h-16 font-bold shadow-md transition-all",
                isClockedIn 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-green-600 hover:bg-green-700 text-white"
              )}
              onClick={() => handleClockAction(isClockedIn ? 'out' : 'in')}
              disabled={loading || !gpsActive || isClockOutDisabled}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Activity className="animate-spin" /> Processing...
                </span>
              ) : (
                isClockedIn 
                  ? (countdown > 0 ? `Wait ${countdown}s` : 'CLOCK OUT') 
                  : 'CLOCK IN'
              )}
            </Button>
            
            <p className="text-xs text-stone-400">
              {isClockedIn 
                ? "Don't forget to clock out before leaving the site." 
                : "Your location will be recorded when you clock in."}
            </p>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-navy-steal">Today's History</h3>
            <span className="text-xs text-stone-500">{new Date().toDateString()}</span>
          </div>
          
          <div className="space-y-3">
            {records.length > 0 ? (
              records.map(record => (
                <div key={record.id} className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 hover:border-slate-200 transition-colors">
                  <div className="flex justify-between items-center mb-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {new Date(record.date).toLocaleDateString()}
                    </Badge>
                    {record.clock_out ? (
                      <Badge variant="secondary" className="bg-stone-100 text-stone-600">
                        Completed {record.work_hours ? `(${record.work_hours}h)` : ''}
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-xs text-stone-400 uppercase font-bold block mb-1">Clock In</span>
                      <div className="font-medium text-navy-steal text-lg">
                        {new Date(record.clock_in).toLocaleTimeString('en-US', {
                          hour: '2-digit', 
                          minute: '2-digit',
                          timeZone: 'Asia/Dubai'
                        })}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-stone-500 mt-1">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[100px]">
                          {parseFloat(record.location_in_lat).toFixed(4)}, {parseFloat(record.location_in_lng).toFixed(4)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-right border-l pl-4">
                      <span className="text-xs text-stone-400 uppercase font-bold block mb-1">Clock Out</span>
                      {record.clock_out ? (
                        <>
                          <div className="font-medium text-navy-steal text-lg">
                            {new Date(record.clock_out).toLocaleTimeString('en-US', {
                              hour: '2-digit', 
                              minute: '2-digit',
                              timeZone: 'Asia/Dubai'
                            })}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-stone-500 mt-1 justify-end">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate max-w-[100px]">
                              {parseFloat(record.location_out_lat).toFixed(4)}, {parseFloat(record.location_out_lng).toFixed(4)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="text-stone-300 italic text-xs">--:--</span>
                      )}
                    </div>
                  </div>

                  {record.notes && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-stone-500">
                        <span className="font-semibold">Note: </span>
                        {record.notes}
                      </p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <p className="text-stone-400 text-sm">No attendance records for today</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AttendancePage;
